import React, { useState, useEffect } from 'react';
import { Filter, LayoutGrid, Plus, PenSquare, Trash2, X } from 'lucide-react';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import { useTheme } from '../context/ThemeContext';
import { message } from 'antd';
import { productsApi } from '../services/api';
import Pagination from '../components/Pagination';
import { formatDate } from '../utils/helpers';

// ======================================================
// ✅ SubcategoryModal — ProductSubcategory ke BAHAR hai
// ✅ Yahi asli fix hai — andar hoga to har render pe recreate hoga
// ======================================================
const SubcategoryModal = ({
  isOpen, onClose, title, onSubmit, isLoading,
  nameValue, onNameChange, categoryValue, onCategoryChange,
  submitLabel, categories, themeColor, isDark
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[10000] p-4">
      <div className={`${isDark ? 'bg-[#1e293b]' : 'bg-white'} rounded-[2rem] w-full max-w-md overflow-hidden shadow-2xl transition-all duration-300`}>
        {/* Header */}
        <div className={`${isDark ? 'bg-white/5' : 'bg-[#D1F2EB]'} px-8 py-4 flex justify-between items-center border-b ${isDark ? 'border-white/10' : 'border-transparent'}`}>
          <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{title}</h2>
          <button onClick={onClose} className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors`}>
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-8 space-y-6">
          {/* Category Select */}
          <div>
            <label className={`block text-sm font-bold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Select product category
            </label>
            <div className="relative">
              <select
                value={categoryValue}
                onChange={(e) => onCategoryChange(e.target.value)}
                className={`w-full appearance-none border rounded-xl px-4 py-3 pr-10 transition-all focus:outline-none focus:ring-2 ${
                  isDark 
                    ? 'bg-white/5 border-white/10 text-white focus:bg-white/10' 
                    : 'bg-white border-gray-300 text-gray-700'
                }`}
                style={{ '--tw-ring-color': themeColor }}
              >
                <option value="" disabled className={isDark ? 'bg-gray-800' : ''}>Select Category</option>
                {(categories || []).map(cat => (
                  <option key={cat.id} value={cat.categoryName || cat.name} className={isDark ? 'bg-gray-800' : ''}>
                    {cat.categoryName || cat.name}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                <svg className="w-4 h-4 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Name Input */}
          <div>
            <label className={`block text-sm font-bold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Product subcategory name
            </label>
            <input
              type="text"
              value={nameValue}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Enter subcategory name"
              className={`w-full border rounded-xl px-4 py-3 transition-all focus:outline-none focus:ring-2 ${
                isDark 
                  ? 'bg-white/5 border-white/10 text-white focus:bg-white/10' 
                  : 'bg-white border-gray-300 text-gray-700'
              }`}
              style={{ '--tw-ring-color': themeColor }}
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end pt-2">
            <button
              onClick={onSubmit}
              disabled={!nameValue.trim() || !categoryValue || isLoading}
              className={`text-white px-10 py-3 rounded-2xl font-bold shadow-lg transition-all
                ${(!nameValue.trim() || !categoryValue || isLoading)
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'hover:brightness-110 active:scale-95'}`}
              style={{
                backgroundColor: (nameValue.trim() && categoryValue && !isLoading)
                  ? themeColor : undefined
              }}
            >
              {isLoading ? 'Please wait...' : submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ======================================================
// ✅ Main Component — yahan se shuru hota hai
// ======================================================
const ProductSubcategory = () => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const [searchTerm, setSearchTerm] = useState('');
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Add Modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [subcategoryNameInput, setSubcategoryNameInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit Modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [editCategoryInput, setEditCategoryInput] = useState('');
  const [editNameInput, setEditNameInput] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Delete Modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [subcategoryToDelete, setSubcategoryToDelete] = useState(null);
  const [deletingSubName, setDeletingSubName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Filter Modal
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // ✅ Debounce — type karte waqt fetch nahi hogi turant
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSubcategories();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (isAddModalOpen || isEditModalOpen) {
      fetchCategories();
    }
  }, [isAddModalOpen, isEditModalOpen]);

  const fetchSubcategories = async () => {
    setLoading(true);
    try {
      const res = await productsApi.getAllProductSubcategories(searchTerm);
      const data = Array.isArray(res) ? res : (res.data || []);
      const mapped = data.map(item => ({
        id: item.id,
        name: item.subCategoryName || 'N/A',
        category: item.categoryName || 'General',
        createdBy: item.createdBy || 'System',
        date: item.createdAt
          ? formatDate(item.createdAt)
          : 'N/A',
        status: item.isActive ?? true,
      }));
      setSubcategories(mapped);
    } catch (err) {
      console.error('Error fetching subcategories:', err);
      setSubcategories([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await productsApi.getActiveProductCategories();
      const list = Array.isArray(res) ? res : (res.data || []);
      setCategories(list);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleAddSubcategory = async () => {
    if (!subcategoryNameInput.trim() || !selectedCategory) return;
    setIsSubmitting(true);
    try {
      await productsApi.createProductSubcategory({
        subCategoryName: subcategoryNameInput.trim(),
        categoryName: selectedCategory,
        createdBy: 'Admin',
      });
      message.success('Subcategory created successfully');
      setSubcategoryNameInput('');
      setSelectedCategory('');
      setIsAddModalOpen(false);
      fetchSubcategories();
    } catch (err) {
      message.error(err.message || 'Failed to create subcategory');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClick = (sub) => {
    setEditNameInput(sub.name || '');
    setEditCategoryInput(sub.category || '');
    setEditingSubcategory(sub);
    setIsEditModalOpen(true);
  };

  const handleUpdateSubcategory = async () => {
    if (!editNameInput.trim() || !editCategoryInput) return;
    setIsUpdating(true);
    try {
      await productsApi.updateProductSubcategory(editingSubcategory.id, {
        subCategoryName: editNameInput.trim(),
        categoryName: editCategoryInput,
        updatedBy: 'Admin',
      });
      message.success('Subcategory updated successfully');
      setIsEditModalOpen(false);
      setEditingSubcategory(null);
      fetchSubcategories();
    } catch (err) {
      message.error(err.message || 'Failed to update subcategory');
    } finally {
      setIsUpdating(false);
    }
  };

  const toggleStatus = async (id) => {
    setSubcategories(prev => prev.map(cat => cat.id === id ? { ...cat, status: !cat.status } : cat));
    try {
      await productsApi.toggleProductSubcategoryStatus(id);
      message.success('Status updated');
    } catch (err) {
      console.error('Error toggling status:', err);
      setSubcategories(prev => prev.map(cat => cat.id === id ? { ...cat, status: !cat.status } : cat));
      message.error('Failed to update status');
    }
  };

  const handleDelete = (subcategory) => {
    setSubcategoryToDelete(subcategory.id);
    setDeletingSubName(subcategory.name);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!subcategoryToDelete) return;
    setIsDeleting(true);
    try {
      await productsApi.deleteProductSubcategory(subcategoryToDelete);
      message.success('Subcategory deleted');
      setSubcategories(prev => prev.filter(s => s.id !== subcategoryToDelete));
      setIsDeleteModalOpen(false);
    } catch (err) {
      console.error('Error deleting subcategory:', err);
      message.error('Failed to delete subcategory');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={`p-6 desktop:p-8 min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#0f172a]' : 'bg-white'}`}>
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h1 className={`text-2xl desktop:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Product subcategory</h1>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <ExpandableSearchBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Search subcategory..."
          />

          <button
            onClick={() => setIsFilterModalOpen(true)}
            className={`p-2 desktop:p-3 border rounded-full transition-colors ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50'}`}
          >
            <Filter className={`w-5 h-5 desktop:w-6 desktop:h-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>

          <button
            onClick={() => setIsAddModalOpen(true)}
            className="p-2 desktop:p-3 text-white rounded-full transition-colors relative group"
            style={{ backgroundColor: currentTheme.color }}
          >
            <LayoutGrid className="w-5 h-5 desktop:w-6 desktop:h-6" />
            <div className="absolute -top-1 -right-1 bg-white rounded-full p-0.5">
              <Plus className="w-2 h-2 desktop:w-3 desktop:h-3" strokeWidth={4} style={{ color: currentTheme.color }} />
            </div>
          </button>
        </div>
      </div>

      {/* Table Section */}
      <div className={`border rounded-xl overflow-hidden transition-all ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-max text-left border-collapse">
            <thead>
              <tr className="text-white text-xs desktop:text-base uppercase font-medium" style={{ backgroundColor: currentTheme.color }}>
                <th className="py-2 px-6 desktop:py-4 desktop:px-8 tracking-wide">ID</th>
                <th className="py-2 px-6 desktop:py-4 desktop:px-8 tracking-wide">NAME</th>
                <th className="py-2 px-6 desktop:py-4 desktop:px-8 tracking-wide">CREATED BY</th>
                <th className="py-2 px-6 desktop:py-4 desktop:px-8 tracking-wide">DATE</th>
                <th className="py-2 px-6 desktop:py-4 desktop:px-8 tracking-wide text-center">STATUS</th>
                <th className="py-2 px-6 desktop:py-4 desktop:px-8 tracking-wide text-center">ACTION</th>
              </tr>
            </thead>
            <tbody className={`${isDark ? 'divide-y divide-white/5' : 'bg-white'}`}>
              {loading ? (
                <tr>
                  <td colSpan="6" className="py-10 text-center text-gray-400">Loading subcategories...</td>
                </tr>
              ) : subcategories.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-10 text-center text-gray-400">No subcategories found</td>
                </tr>
              ) : (
                subcategories
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((sub) => (
                <tr key={sub.id} className={`border-b transition-colors ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'}`}>
                  <td className={`py-2 px-6 desktop:py-4 desktop:px-8 text-sm desktop:text-lg font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{sub.id.toString().slice(-4)}</td>
                  <td className={`py-2 px-6 desktop:py-4 desktop:px-8 text-sm desktop:text-lg font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>{sub.name}</td>
                  <td className={`py-2 px-6 desktop:py-4 desktop:px-8 text-sm desktop:text-lg ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>{sub.createdBy}</td>
                  <td className={`py-2 px-6 desktop:py-4 desktop:px-8 text-sm desktop:text-lg ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>{sub.date}</td>

                  <td className="py-2 px-6 desktop:py-4 desktop:px-8 text-center">
                    <button
                      onClick={() => toggleStatus(sub.id)}
                      className={`relative w-11 h-6 desktop:w-14 desktop:h-8 rounded-full transition-all duration-300 ease-in-out focus:outline-none border-2`}
                      style={{ 
                        backgroundColor: sub.status ? currentTheme.color : '#ffffff',
                        borderColor: isDark ? '#ffffff' : '#D1D5DB',
                      }}
                    >
                      <span
                        className={`absolute top-[2px] left-[2px] w-4 h-4 desktop:w-6 desktop:h-6 rounded-full shadow-md transition-all duration-300 ease-in-out transform border-2 border-white ${
                          sub.status 
                            ? 'translate-x-5 desktop:translate-x-6 bg-white' 
                            : 'translate-x-0 bg-gray-400'
                        }`}
                      />
                    </button>
                  </td>

                  <td className="py-2 px-6 desktop:py-4 desktop:px-8 text-center">
                    <div className="flex items-center justify-center gap-3">
                      <button
                        onClick={() => handleEditClick(sub)}
                        className="p-1 desktop:p-2 transition-colors"
                        style={{ color: '#F59E0B' }}
                      >
                        <PenSquare className="w-5 h-5 desktop:w-6 desktop:h-6" />
                      </button>
                      <button
                        onClick={() => handleDelete(sub)}
                        className="p-1 desktop:p-2 text-red-500 hover:text-red-700 transition-colors"
                      >
                        <Trash2 className="w-5 h-5 desktop:w-6 desktop:h-6" />
                      </button>
                    </div>
                  </td>
                    </tr>
                  )))
              }
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-4">
        <Pagination 
          currentPage={currentPage}
          totalItems={subcategories.length}
          itemsPerPage={itemsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>

      {/* Add Modal — SubcategoryModal bahar se aa raha hai */}
      <SubcategoryModal
        isOpen={isAddModalOpen}
        onClose={() => { setIsAddModalOpen(false); setSubcategoryNameInput(''); setSelectedCategory(''); }}
        title="Add New Product SubCategory"
        onSubmit={handleAddSubcategory}
        isLoading={isSubmitting}
        nameValue={subcategoryNameInput}
        onNameChange={setSubcategoryNameInput}
        categoryValue={selectedCategory}
        onCategoryChange={setSelectedCategory}
        submitLabel="Submit"
        categories={categories}
        themeColor={currentTheme.color}
        isDark={isDark}
      />

      {/* ✅ Edit Modal — SubcategoryModal bahar se aa raha hai */}
      <SubcategoryModal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setEditingSubcategory(null); setEditNameInput(''); setEditCategoryInput(''); }}
        title="Edit Product SubCategory"
        onSubmit={handleUpdateSubcategory}
        isLoading={isUpdating}
        nameValue={editNameInput}
        onNameChange={setEditNameInput}
        categoryValue={editCategoryInput}
        onCategoryChange={setEditCategoryInput}
        submitLabel="Update"
        categories={categories}
        themeColor={currentTheme.color}
        isDark={isDark}
      />

      {/* Delete Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-4">
          <div className={`${isDark ? 'bg-[#1e293b]' : 'bg-white'} rounded-2xl w-full max-w-md p-8 text-center shadow-2xl transition-all duration-300`}>
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4 text-red-600">
              <Trash2 className="w-8 h-8" />
            </div>
            <h3 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Delete Subcategory?</h3>
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} mb-8`}>Are you sure you want to delete <b>{deletingSubName}</b>? This action cannot be undone.</p>
            <div className="flex gap-4">
              <button onClick={() => setIsDeleteModalOpen(false)} className={`flex-1 py-3 px-4 font-bold rounded-xl transition-all ${isDark ? 'bg-white/5 text-gray-300 hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Cancel</button>
              <button onClick={confirmDelete} disabled={isDeleting} className="flex-1 py-3 px-4 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 shadow-lg transition-all disabled:opacity-50">
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {isFilterModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className={`${isDark ? 'bg-[#1e293b]' : 'bg-white'} rounded-2xl w-full max-w-md p-6 shadow-2xl`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Filter Options</h2>
              <X className={`cursor-pointer ${isDark ? 'text-gray-400' : 'text-gray-500'}`} onClick={() => setIsFilterModalOpen(false)} />
            </div>
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} italic`}>Filter implementation coming soon...</p>
            <button onClick={() => setIsFilterModalOpen(false)} className="w-full mt-6 py-3 text-white font-bold rounded-xl transition-all active:scale-95 hover:brightness-110" style={{ background: currentTheme.color }}>Close</button>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProductSubcategory;